import type { Entity } from '@backstage/catalog-model';

/**
 * THE single source for the regask.io/deployments annotation: key, canonical
 * environment order and the parsers — shared by the catalog entity table,
 * the Home page health dots and the entity page Deployments card.
 */
export const DEPLOYMENTS_ANNOTATION = 'regask.io/deployments';

/** Canonical environment order (selector queries, table columns, card rows). */
export const ENVIRONMENTS = [
  'development',
  'staging',
  'pre-prod',
  'production',
] as const;

export interface HistoryEntry {
  tag: string;
  publishedAt: string;
  commitUrl?: string;
}

export interface DeploymentRow {
  env: string;
  tag: string;
  argocdApp: string;
  argocdUrl: string;
  commitUrl?: string;
  sourceRepo?: string;
  publishedAt?: string;
  logsUrl?: string;
  tracesUrl?: string;
  /** Optional: entities emitted before profiling links existed lack it. */
  profilesUrl?: string;
  history?: HistoryEntry[];
}

export interface EnvDeployment {
  tag: string;
  argocdApp?: string;
}

function parseAnnotation(entity: Entity): Record<string, any> | undefined {
  const raw = entity.metadata?.annotations?.[DEPLOYMENTS_ANNOTATION];
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/** Parse the regask.io/deployments annotation into ordered rows. */
export function parseDeployments(entity: Entity): DeploymentRow[] {
  const parsed = parseAnnotation(entity);
  if (!parsed) return [];

  const toRow = (env: string, d: any): DeploymentRow => ({
    env,
    tag: d.tag ?? '',
    argocdApp: d.argocdApp ?? '',
    argocdUrl: d.argocdUrl ?? '',
    commitUrl: d.commitUrl,
    sourceRepo: d.sourceRepo,
    publishedAt: d.publishedAt,
    logsUrl: d.logsUrl,
    tracesUrl: d.tracesUrl,
    profilesUrl: d.profilesUrl,
    history: Array.isArray(d.history) ? d.history : undefined,
  });

  const rows: DeploymentRow[] = [];

  // Iterate in canonical order, skip envs not in annotation
  for (const env of ENVIRONMENTS) {
    if (!parsed[env]) continue;
    rows.push(toRow(env, parsed[env]));
  }

  // Any envs in annotation not in canonical order append at the end
  for (const env of Object.keys(parsed)) {
    if (!(ENVIRONMENTS as readonly string[]).includes(env)) {
      rows.push(toRow(env, parsed[env]));
    }
  }

  return rows;
}

/**
 * Extract the {tag, argocdApp} pair for one environment from the
 * regask.io/deployments annotation. Returns undefined when the entity has no
 * deployment for that environment (cell renders '—').
 */
export function getEnvDeployment(
  entity: Entity,
  env: string,
): EnvDeployment | undefined {
  const parsed = parseAnnotation(entity);
  const d = parsed?.[env];
  if (!d || typeof d !== 'object') return undefined;
  return { tag: d.tag ?? '', argocdApp: d.argocdApp };
}
